import { useState, useRef, useCallback } from 'react';
import { IWatrTrip } from '@nkr/models/watr-trip';
import { ITripGroup } from '../types/trip.types';
import { ILineItem } from '../types/line-item.types';
import { groupTripsByPhase, findFirstPendingTripIndex } from '../services/trip.service';
import { processLineItems } from '../services/lineItem.service';
import { recalculateGrandTotal, IGrandTotal } from '../utils/calculation.utils';

interface UseTripsDataProps {
    getTripsByTravelRequestId: (travelRequestId: number) => Promise<any>;
    getLineItemsByTripId: (tripId: number) => Promise<any>;
    onProcessLineItems: (items: ILineItem[]) => void;
    onRecalculateGrandTotal: (total: IGrandTotal) => void;
    onTripsLoaded?: (groups: ITripGroup[], firstPendingIndex: number | null) => void;
}

export const useTripsData = (props: UseTripsDataProps) => {
    const [tripGroups, setTripGroups] = useState<ITripGroup[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const abortControllerRef = useRef<AbortController | null>(null);

    const loadTrips = useCallback(async (travelRequestId: number, shouldAutoOpenSidebar: boolean = true) => {
        if (!travelRequestId) {
            setIsLoading(false);
            return;
        }

        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        setIsLoading(true);
        try {
            const tripsResponse = await props.getTripsByTravelRequestId(travelRequestId);
            const tripsData = (tripsResponse.data?.Results || tripsResponse.data?.results) as IWatrTrip[] || [];
            
            if (tripsData.length === 0) {
                setTripGroups([]);
                setIsLoading(false);
                return;
            }
            
            const lineItemsMap = new Map<number, ILineItem[]>();
            for (const trip of tripsData) {
                if (abortControllerRef.current?.signal.aborted) {
                    return;
                }
                try {
                    const lineItemsResponse = await props.getLineItemsByTripId(trip.id);
                    const items = (lineItemsResponse.data?.Results || lineItemsResponse.data?.results) as ILineItem[] || [];
                    lineItemsMap.set(trip.id, items);
                    props.onProcessLineItems(items);
                } catch (err) {
                    if (!abortControllerRef.current?.signal.aborted) {
                        lineItemsMap.set(trip.id, []);
                    }
                }
            }

            if (abortControllerRef.current?.signal.aborted) {
                return;
            }

            const groups = groupTripsByPhase(tripsData, lineItemsMap);
            setTripGroups(groups);
            if (groups.length > 0) {
                const grandTotal = recalculateGrandTotal(groups);
                props.onRecalculateGrandTotal(grandTotal);
                
                const firstPendingIndex = findFirstPendingTripIndex(groups);
                if (props.onTripsLoaded) {
                    props.onTripsLoaded(groups, firstPendingIndex);
                }
            }
            setIsLoading(false);
        } catch (err) {
            if (!abortControllerRef.current?.signal.aborted) {
                setTripGroups([]);
                setIsLoading(false);
            }
        }
    }, [props]);

    const refreshTrips = useCallback(async (travelRequestId: number) => {
        await loadTrips(travelRequestId, false);
    }, [loadTrips]);

    const loadTripsAndReturnGroups = useCallback(async (travelRequestId: number): Promise<ITripGroup[]> => {
        if (!travelRequestId) {
            setIsLoading(false);
            return [];
        }

        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        setIsLoading(true);
        try {
            const tripsResponse = await props.getTripsByTravelRequestId(travelRequestId);
            const tripsData = (tripsResponse.data?.Results || tripsResponse.data?.results) as IWatrTrip[] || [];
            
            if (tripsData.length === 0) {
                setTripGroups([]);
                setIsLoading(false);
                return [];
            }
            
            const lineItemsMap = new Map<number, ILineItem[]>();
            for (const trip of tripsData) {
                if (abortControllerRef.current?.signal.aborted) {
                    return [];
                }
                try {
                    const lineItemsResponse = await props.getLineItemsByTripId(trip.id);
                    const items = (lineItemsResponse.data?.Results || lineItemsResponse.data?.results) as ILineItem[] || [];
                    lineItemsMap.set(trip.id, items);
                    props.onProcessLineItems(items);
                } catch (err) {
                    if (!abortControllerRef.current?.signal.aborted) {
                        lineItemsMap.set(trip.id, []);
                    }
                }
            }

            if (abortControllerRef.current?.signal.aborted) {
                return [];
            }

            const groups = groupTripsByPhase(tripsData, lineItemsMap);
            setTripGroups(groups);
            if (groups.length > 0) {
                const grandTotal = recalculateGrandTotal(groups);
                props.onRecalculateGrandTotal(grandTotal);
                
                const firstPendingIndex = findFirstPendingTripIndex(groups);
                if (props.onTripsLoaded) {
                    props.onTripsLoaded(groups, firstPendingIndex);
                }
            }
            setIsLoading(false);
            return groups;
        } catch (err) {
            if (!abortControllerRef.current?.signal.aborted) {
                setTripGroups([]);
                setIsLoading(false);
            }
            return [];
        }
    }, [props]);

    return {
        tripGroups,
        isLoading,
        loadTrips,
        refreshTrips,
        loadTripsAndReturnGroups,
        setTripGroups,
        setIsLoading
    };
};

